import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { ContactForm } from "@/components/contact-form";
import bukLogo from "@/assets/buk-logo.png.asset.json";

// Link de agendamiento/cotización — editable (pendiente de confirmación con Buk)
const CTA_URL = "/contacto/formulario";
const CANONICAL = "https://mine-class.com/cursos/empresas/buk";

const TITLE = "Buk x Mine Class: 30% de descuento en cursos para empresas";
const DESCRIPTION =
  "Beneficio exclusivo para clientes Buk: cursos e-learning de seguridad minera y SST con 30% de descuento, disponibles 24/7, con certificación digital y validación de identidad por IA.";

export const Route = createFileRoute("/cursos/empresas/buk")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      {
        name: "keywords",
        content:
          "Buk, beneficio Buk, descuento Buk, cursos Buk, capacitación empresas Buk, Mine Class, cursos e-learning minería, cursos SST Chile",
      },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Offer",
          name: "Beneficio Buk x Mine Class",
          description: DESCRIPTION,
          url: CANONICAL,
          priceCurrency: "CLP",
          eligibleCustomerType: "Business",
          seller: { "@type": "Organization", name: "Mine Class", url: "https://mine-class.com" },
          itemOffered: {
            "@type": "Course",
            name: "Cursos e-learning de seguridad y minería",
            description:
              "Catálogo de cursos asincrónicos de seguridad y salud ocupacional para la industria minera.",
            provider: { "@type": "Organization", name: "Mine Class", url: "https://mine-class.com" },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "¿Quiénes acceden al descuento Buk?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Todos los clientes de Buk acceden a un 30% de descuento en el catálogo de cursos e-learning de Mine Class.",
              },
            },
            {
              "@type": "Question",
              name: "¿Cómo se dictan los cursos?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Son cursos asincrónicos disponibles 24/7 desde cualquier dispositivo, con certificación digital y validación de identidad con IA.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: BukContent,
});

const BENEFITS = [
  { icon: "schedule", title: "Cursos 24/7", text: "Acceso permanente, a tu ritmo y desde cualquier dispositivo." },
  {
    icon: "workspace_premium",
    title: "Certificación digital",
    text: "Certificados emitidos y almacenados digitalmente, siempre disponibles.",
  },
  {
    icon: "face_retouching_natural",
    title: "Validación de identidad con IA",
    text: "Trazabilidad real en cada proceso de capacitación y evaluación.",
  },
];

const COURSES = [
  { name: "Uso de extintores (teórico)", list: 30000, buk: 21000 },
  { name: "Manejo a la Defensiva", list: 34500, buk: 24150 },
  { name: "Riesgos Eléctricos", list: 27500, buk: 19250 },
  { name: "EPP General", list: 20000, buk: 14000 },
  { name: "Hidráulica Aplicada a Procesos Mineros I", list: 64000, buk: 44800 },
  { name: "Hidráulica Aplicada a Procesos Mineros II", list: 64000, buk: 44800 },
];

const clp = (n: number) => `$${n.toLocaleString("es-CL")}`;

function BukContent() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="space-y-10">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-2xl bg-[#3B1164] text-white px-6 py-10 md:px-10 md:py-12">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -top-14 -right-10 w-56 h-56 md:w-80 md:h-80 rounded-[2.5rem] bg-[#F05948]/25 rotate-[18deg]" />
          <div className="absolute top-16 right-24 w-32 h-32 md:w-52 md:h-52 rounded-[2rem] border border-white/20 rotate-[18deg]" />
        </div>
        <div className="relative">
          <div className="flex flex-wrap items-center gap-4">
            <img
              src={bukLogo.url}
              alt="Logo Buk"
              width={110}
              height={43}
              loading="eager"
              className="h-8 w-auto"
            />
            <span className="text-white/40" aria-hidden="true">
              ×
            </span>
            <span className="text-lg font-bold tracking-tight">Mine Class</span>
          </div>
          <span className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#F05948] px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest whitespace-pre-line text-center">
            RED DE PROVEEDORES SST{"\n"}|  EXCLUSIVO CLIENTES BUK
          </span>
          <h2 className="mt-4 text-2xl md:text-4xl font-bold leading-tight max-w-2xl">
            Capacita a tu equipo con 30% de descuento
          </h2>
          <p className="mt-3 text-sm md:text-base text-white/80 max-w-2xl leading-relaxed">
            Cursos e-learning SST y minería, disponibles 24/7, con certificación digital. Beneficio
            exclusivo para clientes de Buk.
          </p>
          <a
            href="#catalogo-buk"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#F05948] px-6 py-3 min-h-[44px] text-xs font-bold uppercase tracking-widest text-white hover:brightness-110 transition"
          >
            Ver catálogo de cursos
            <span className="material-symbols-outlined text-base" aria-hidden="true">
              arrow_downward
            </span>
          </a>
        </div>
      </section>

      {/* Beneficios */}
      <section>
        <h2 className="text-xl md:text-2xl font-bold text-[#3B1164]">Por qué Mine Class</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map((b) => (
            <article
              key={b.title}
              className="rounded-2xl border border-[#3B1164]/10 bg-white p-5 hover:border-[#F05948]/50 transition-colors"
            >
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#F05948]/10 text-[#F05948]">
                <span className="material-symbols-outlined" aria-hidden="true">
                  {b.icon}
                </span>
              </span>
              <h3 className="mt-3 text-base font-bold text-[#3B1164]">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#44404F]">{b.text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Catálogo */}
      <section id="catalogo-buk" className="scroll-mt-28">
        <h2 className="text-xl md:text-2xl font-bold text-[#3B1164]">
          Catálogo de cursos con descuento Buk
        </h2>
        <p className="mt-2 text-sm text-[#44404F]">
          Modalidad asincrónica · disponible en todo Chile ·{" "}
          <strong className="text-[#F05948]">30% de descuento</strong>
        </p>

        <div className="mt-6 hidden md:block overflow-hidden rounded-2xl border border-[#3B1164]/10 bg-white">
          <table className="w-full text-left border-collapse">
            <caption className="sr-only">Cursos disponibles con precio lista y precio Buk</caption>
            <thead>
              <tr className="bg-[#3B1164] text-white">
                <th scope="col" className="px-5 py-3.5 text-xs font-bold uppercase tracking-widest">
                  Curso
                </th>
                <th scope="col" className="px-5 py-3.5 text-xs font-bold uppercase tracking-widest">
                  Valor lista
                </th>
                <th scope="col" className="px-5 py-3.5 text-xs font-bold uppercase tracking-widest">
                  Precio Buk
                </th>
              </tr>
            </thead>
            <tbody>
              {COURSES.map((c) => (
                <tr key={c.name} className="border-t border-[#3B1164]/10">
                  <th scope="row" className="px-5 py-3.5 font-semibold text-[#3B1164]">
                    {c.name}
                  </th>
                  <td className="px-5 py-3.5 text-[#44404F] line-through opacity-60">{clp(c.list)}</td>
                  <td className="px-5 py-3.5 font-bold text-[#F05948]">{clp(c.buk)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ul className="mt-6 grid gap-3 md:hidden">
          {COURSES.map((c) => (
            <li key={c.name} className="rounded-2xl border border-[#3B1164]/10 bg-white p-4">
              <p className="font-semibold text-[#3B1164]">{c.name}</p>
              <div className="mt-2 flex items-baseline gap-3">
                <span className="text-sm text-[#44404F] line-through opacity-60">{clp(c.list)}</span>
                <span className="text-lg font-bold text-[#F05948]">{clp(c.buk)}</span>
              </div>
            </li>
          ))}
        </ul>

        <p className="mt-3 text-xs text-[#44404F]/70">Valores por persona en pesos chilenos.</p>
      </section>

      {/* No cliente Buk */}
      <section className="rounded-2xl border border-[#3B1164]/10 bg-white p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-[#F05948]">Este descuento es exclusivo para clientes de Buk.</p>
            <h3 className="mt-1 text-lg md:text-xl font-bold text-[#3B1164]">Haz click y obten el descuento</h3>
          </div>
          <a
            href="https://www.buk.cl/red-de-proveedores-sst"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#3B1164] px-6 py-3 min-h-[44px] text-xs font-bold uppercase tracking-widest text-[#3B1164] hover:bg-[#3B1164] hover:text-white transition"
          >
            Conoce Buk
            <span className="material-symbols-outlined text-base" aria-hidden="true">open_in_new</span>
          </a>
        </div>
      </section>


      {/* Modal formulario */}
      {modalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Formulario de contacto Descuentos Buk"
          className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModalOpen(false);
          }}
        >
          <div className="relative w-full max-w-2xl rounded-2xl bg-white p-6 md:p-10 shadow-2xl my-8">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              aria-label="Cerrar formulario"
              className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full text-[var(--color-on-surface-variant)] hover:bg-[var(--color-surface-container-low)]"
            >
              <span className="material-symbols-outlined" aria-hidden="true">close</span>
            </button>
            <h2 className="mb-2 text-xl md:text-2xl font-bold text-[var(--color-brand-purple)]">
              Cotiza con el beneficio Buk
            </h2>
            <p className="mb-6 text-sm text-[var(--color-on-surface-variant)]">
              Completa tus datos y un especialista te contactará con el descuento aplicado.
            </p>
            <ContactForm
              defaultInterest="Descuentos BUK"
              submitLabel="Solicitar propuesta"
              onSuccess={() => setTimeout(() => setModalOpen(false), 2500)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
